import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { 
  Users, 
  UserCheck, 
  Clock, 
  FileText, 
  Plus,
  CheckCircle,
  XCircle,
  AlertCircle,
  Bell,
  Calendar,
  Receipt,
  Wifi,
  User,
  MessageSquare,
  Mail
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

export default function Dashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [myApplications, setMyApplications] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadMessages, setUnreadMessages] = useState([]);
  const [showMessageNotification, setShowMessageNotification] = useState(false);

  useEffect(() => {
    fetchData();
    
    // Check for unread messages for tenants
    if (user?.user_type === "tenant") {
      fetchUnreadMessages();
    }
    
    // Set up heartbeat interval (every 2 minutes) and online users refresh (every 30 seconds)
    const heartbeatInterval = setInterval(() => {
      axios.post(`${API}/auth/heartbeat`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
    }, 120000); // 2 minutes
    
    let onlineUsersInterval;
    if (user?.role === "admin") {
      onlineUsersInterval = setInterval(fetchOnlineUsers, 30000); // 30 seconds
    }
    
    return () => {
      clearInterval(heartbeatInterval);
      if (onlineUsersInterval) clearInterval(onlineUsersInterval);
    };
  }, []);

  const fetchUnreadMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages/unread`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.length > 0) {
        setUnreadMessages(response.data);
        setShowMessageNotification(true);
      }
    } catch (error) {
      console.error("Failed to fetch unread messages:", error);
    }
  };

  const fetchOnlineUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/online-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOnlineUsers(response.data);
    } catch (error) {
      // Silently fail - user might not be admin
    }
  };

  const fetchData = async () => {
    try {
      if (user?.role === "admin" || user?.role === "property_manager" || user?.role === "manager") {
        // Fetch admin stats
        const response = await axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(response.data);
        // Also fetch online users (admin only)
        if (user?.role === "admin") {
          fetchOnlineUsers();
        }
      } else {
        // Fetch user's applications
        const response = await axios.get(`${API}/applications/my`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMyApplications(response.data);
      }
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Approved":
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Approved</Badge>;
      case "Pending Approval":
        return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">Pending</Badge>;
      case "Denied":
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Denied</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Approved":
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case "Pending Approval":
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case "Denied":
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-stone-400" />;
    }
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="dashboard">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 pt-20 md:pt-6 md:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-primary">
              {(user?.role === "admin" || user?.role === "property_manager" || user?.role === "manager") ? "Dashboard" : "Dashboard"}
            </h1>
            <p className="text-text-muted mt-1">
              Welcome back, {user?.full_name || user?.username}
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-pulse text-primary">Loading...</div>
            </div>
          ) : (user?.role === "admin" || user?.role === "property_manager" || user?.role === "manager") ? (
            // Admin/Manager Dashboard
            <div className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                <Card className="border-stone-200" data-testid="stat-registrants">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-text-muted">Total Registrants</p>
                        <p className="text-3xl font-bold text-primary mt-1">{stats?.total_registrants || 0}</p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Users className="w-6 h-6 text-primary" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-stone-200" data-testid="stat-tenants">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-text-muted">Approved Tenants</p>
                        <p className="text-3xl font-bold text-green-600 mt-1">{stats?.total_tenants || 0}</p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                        <UserCheck className="w-6 h-6 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-stone-200" data-testid="stat-pending">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-text-muted">Pending Approval</p>
                        <p className="text-3xl font-bold text-yellow-600 mt-1">{stats?.pending_applications || 0}</p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                        <Clock className="w-6 h-6 text-yellow-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-stone-200" data-testid="stat-denied">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-text-muted">Denied Applications</p>
                        <p className="text-3xl font-bold text-red-600 mt-1">{stats?.denied_applications || 0}</p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                        <XCircle className="w-6 h-6 text-red-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-stone-200 border-amber-200 bg-amber-50/30" data-testid="stat-upcoming-payments">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-text-muted">Payments Due (7d)</p>
                        <p className="text-3xl font-bold text-amber-600 mt-1">{stats?.upcoming_payments_7_days || 0}</p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-amber-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions */}
              <Card className="border-stone-200">
                <CardHeader>
                  <CardTitle className="text-lg">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Link to="/admin/registrants">
                      <Button variant="outline" className="w-full h-20 flex flex-col items-center justify-center gap-2 hover:bg-stone-50" data-testid="quick-action-registrants">
                        <Users className="w-6 h-6 text-primary" />
                        <span>View Registrants</span>
                      </Button>
                    </Link>
                    <Link to="/admin/pending-approval">
                      <Button variant="outline" className="w-full h-20 flex flex-col items-center justify-center gap-2 hover:bg-stone-50" data-testid="quick-action-pending">
                        <Clock className="w-6 h-6 text-yellow-600" />
                        <span>Pending Applications</span>
                      </Button>
                    </Link>
                    <Link to="/admin/tenants">
                      <Button variant="outline" className="w-full h-20 flex flex-col items-center justify-center gap-2 hover:bg-stone-50" data-testid="quick-action-tenants">
                        <UserCheck className="w-6 h-6 text-green-600" />
                        <span>View Tenants</span>
                      </Button>
                    </Link>
                    <Link to="/admin/payment-confirmations">
                      <Button variant="outline" className="w-full h-20 flex flex-col items-center justify-center gap-2 hover:bg-amber-50 border-amber-200" data-testid="quick-action-payments">
                        <Receipt className="w-6 h-6 text-amber-600" />
                        <span>Payment Confirmations</span>
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>

              {/* Online Users - Admin Only */}
              {user?.role === "admin" && (
              <Card className="border-stone-200 border-green-200 bg-green-50/30" data-testid="online-users-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Wifi className="w-5 h-5 text-green-600" />
                    Currently Online
                    <Badge className="bg-green-100 text-green-700 ml-2">{onlineUsers.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {onlineUsers.length === 0 ? (
                    <p className="text-text-muted text-sm">No users currently online</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {onlineUsers.map((onlineUser) => (
                        <div 
                          key={onlineUser.id}
                          className="flex items-center gap-3 p-3 bg-white rounded-lg border border-green-200"
                        >
                          <div className="relative">
                            {onlineUser.profile_photo ? (
                              <img 
                                src={`${API}${onlineUser.profile_photo}`}
                                alt={onlineUser.full_name}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center">
                                <User className="w-5 h-5 text-stone-500" />
                              </div>
                            )}
                            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{onlineUser.full_name}</p>
                            <p className="text-xs text-text-muted truncate">
                              {onlineUser.role === "property_manager" ? "Property Manager" : 
                               onlineUser.user_type === "tenant" ? "Tenant" : 
                               onlineUser.user_type || "User"}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              )}
            </div>
          ) : (
            // User Dashboard
            <div className="space-y-6">
              {/* User Status */}
              <Card className="border-stone-200">
                <CardHeader>
                  <CardTitle className="text-lg">Your Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    {getStatusIcon(user?.status)}
                    <div>
                      <p className="font-medium">{user?.status || "Pending Approval"}</p>
                      <p className="text-sm text-text-muted">
                        {user?.status === "Approved" 
                          ? "Your application has been approved. Welcome to WilyRash Homes!"
                          : user?.status === "Denied"
                          ? "Your application was not approved. Please contact the administrator."
                          : "Your application is being reviewed."
                        }
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* My Applications */}
              <Card className="border-stone-200">
                <CardHeader>
                  <CardTitle className="text-lg">My Applications</CardTitle>
                </CardHeader>
                <CardContent>
                  {myApplications.length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                      <p className="text-text-muted">No applications found.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {myApplications.map((app) => (
                        <div 
                          key={app.id} 
                          className="flex items-center justify-between p-4 bg-stone-50 rounded-xl"
                          data-testid={`application-${app.id}`}
                        >
                          <div className="flex items-center gap-4">
                            {getStatusIcon(app.status)}
                            <div>
                              <p className="font-medium">{app.full_name}</p>
                              <p className="text-sm text-text-muted">
                                Submitted: {new Date(app.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          {getStatusBadge(app.status)}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      {/* Unread Messages Notification Dialog for Tenants - MUST acknowledge to continue */}
      <Dialog open={showMessageNotification} onOpenChange={() => {}}>
        <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-primary">
              <Mail className="w-5 h-5" />
              Important: You Have New Messages
            </DialogTitle>
            <DialogDescription>
              You have {unreadMessages.length} unread message{unreadMessages.length > 1 ? 's' : ''} from the administrator. 
              <span className="block mt-2 font-medium text-amber-600">
                Please read and acknowledge these messages to continue.
              </span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-3 max-h-60 overflow-y-auto">
            {unreadMessages.slice(0, 3).map((msg) => (
              <div 
                key={msg.id}
                className="bg-blue-50 rounded-xl p-3 border border-blue-200"
              >
                <p className="font-medium text-sm text-primary">{msg.subject}</p>
                <p className="text-xs text-stone-500 mt-1 line-clamp-2">{msg.content}</p>
              </div>
            ))}
            {unreadMessages.length > 3 && (
              <p className="text-sm text-stone-500 text-center">
                +{unreadMessages.length - 3} more message{unreadMessages.length - 3 > 1 ? 's' : ''}
              </p>
            )}
          </div>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            <strong>Note:</strong> You must read and acknowledge your messages before accessing other features.
          </div>
          
          <DialogFooter>
            <Button 
              onClick={() => {
                setShowMessageNotification(false);
                navigate("/messages");
              }}
              className="w-full bg-primary hover:bg-primary-800"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Read & Acknowledge Messages
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
